import { isSmallScreen, prefersReducedMotion } from '@/effects/motion';
import { gsap } from 'gsap';

/**
 * Custom magnetic cursor (desktop only, respects prefers-reduced-motion).
 *
 * Renders two elements:
 *  – .cursor-outer : large blurred circle that follows with soft lag
 *  – .cursor-inner : tiny sharp dot at exact cursor position
 *
 * Interactive elements (.tilt-card, a, button) trigger a magnetic pull
 * and scale up the outer circle for a premium "hover" feedback.
 */
export const initCursor = (): (() => void) => {
  if (isSmallScreen() || prefersReducedMotion()) return () => {};

  // Touch devices: no custom cursor needed
  if (window.matchMedia('(pointer: coarse)').matches) return () => {};

  // ── Create DOM elements ──
  const outer = document.createElement('div');
  outer.className = 'cursor-outer';

  const inner = document.createElement('div');
  inner.className = 'cursor-inner';

  document.body.appendChild(outer);
  document.body.appendChild(inner);

  // ── State ──
  let mx = window.innerWidth / 2;
  let my = window.innerHeight / 2;
  let lx = mx;
  let ly = my;

  // ── Event listeners ──
  const onMove = (e: MouseEvent): void => {
    mx = e.clientX;
    my = e.clientY;
    gsap.set(inner, { x: mx, y: my });
  };

  document.addEventListener('mousemove', onMove);

  // GSAP ticker for smooth outer ring
  const trackOuter = (): void => {
    lx += (mx - lx) * 0.13;
    ly += (my - ly) * 0.13;
    gsap.set(outer, { x: lx, y: ly });
  };
  gsap.ticker.add(trackOuter);

  // Hide on leave / show on enter document
  const onLeave = (): void => {
    gsap.to([outer, inner], { opacity: 0, duration: 0.3 });
  };
  const onEnter = (): void => {
    gsap.to([outer, inner], { opacity: 1, duration: 0.3 });
  };
  document.addEventListener('mouseleave', onLeave);
  document.addEventListener('mouseenter', onEnter);

  // ── Magnetic hover effect ──
  const MAGNETIC_SELECTOR = 'a, button, .tilt-card, [data-magnetic]';
  const magneticEls = document.querySelectorAll<HTMLElement>(MAGNETIC_SELECTOR);

  type MagHandler = {
    el: HTMLElement;
    enter: () => void;
    leave: () => void;
    move: (e: MouseEvent) => void;
  };
  const handlers: MagHandler[] = [];

  magneticEls.forEach(el => {
    const enter = (): void => {
      gsap.to(outer, { scale: 2.2, opacity: 0.55, duration: 0.35, ease: 'power2.out' });
    };

    const leave = (): void => {
      gsap.to(outer, { scale: 1, opacity: 1, duration: 0.45, ease: 'power2.out' });
      gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1, 0.45)' });
    };

    const move = (e: MouseEvent): void => {
      const rect = el.getBoundingClientRect();
      const dx = e.clientX - (rect.left + rect.width / 2);
      const dy = e.clientY - (rect.top + rect.height / 2);
      gsap.to(el, { x: dx * 0.25, y: dy * 0.25, duration: 0.25, ease: 'power2.out' });
    };

    el.addEventListener('mouseenter', enter);
    el.addEventListener('mouseleave', leave);
    el.addEventListener('mousemove', move);
    handlers.push({ el, enter, leave, move });
  });

  // Hide system cursor
  document.documentElement.style.cursor = 'none';

  return (): void => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseleave', onLeave);
    document.removeEventListener('mouseenter', onEnter);
    gsap.ticker.remove(trackOuter);
    handlers.forEach(({ el, enter, leave, move }) => {
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('mouseleave', leave);
      el.removeEventListener('mousemove', move);
      gsap.killTweensOf(el);
      gsap.set(el, { x: 0, y: 0 });
    });
    outer.remove();
    inner.remove();
    document.documentElement.style.cursor = '';
  };
};
